import { useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { CardHeader } from "reactstrap";
import Button from "../../../components/Button";
import Drawer from "../../../components/Drawer";
import Table from "../../../components/NTable";
import { useNewTable } from "../../../components/NTable/hook";
import { formatParams } from "../../../utils/api/format";
import { useAccess, useActionWithAccess } from "../../../utils/hooks/access";
import { useInitialState } from "../../../utils/hooks/useInitialState";
import { useVisible } from "../../../utils/hooks/useVisible";
import { useDeleteModal } from "../../Settings/helpers/useDeleteModal";
import { useQueryFilters } from "../../Settings/helpers/useQueryFilters";
import { useProfile } from "../context";
import { usePBJApi } from "./api";
import { useColumns } from "./columns";
import PBJForm from "./Form";

const useCustomDrawer = (getChildren) => {
    const [wrapperElement, setWrapperElement] = useState(null);

    useLayoutEffect(() => {
        const element = document.getElementById("pbj-drawer");
        setWrapperElement(element);
    }, [setWrapperElement]);

    if (wrapperElement === null) return null;

    return createPortal(getChildren(), wrapperElement);
};

function PBJ({ isOpenFromTimesheet }) {
    const { t } = useTranslation();
    const { visible, open, close, selected } = useVisible();
    const { fetch, data, totalItems, post, edit, deletePbj, loading } = usePBJApi();
    const { isSelf } = useProfile();

    const { modal: deleteModal, open: _openDeleteModal } = useDeleteModal({
        message: () => t("do-you-want-to-delete-plan"),
        onConfirm: (id) => deletePbj(id),
        permission: isSelf ? "profile.pbjCode.canDelete" : "people.pbjCode.canDelete"
    });
    const openDeleteModal = isOpenFromTimesheet ? undefined : _openDeleteModal;

    const mode = selected ? "edit" : "create";
    const _update = useActionWithAccess(open, isSelf ? "profile.pbjCode.canEdit" : "people.pbjCode.canEdit");
    const update = isOpenFromTimesheet ? undefined : _update;
    const columns = useColumns({
        edit: update,
        remove: openDeleteModal
    });

    const table = useNewTable({
        data,
        totalItems,
        columns,
        loading,
        enableRowSelection: false,
        initialState: useInitialState(() => ({
            sorting: [{ id: "startEffectiveDate", desc: true }],
        })),
    });

    const queryFilters = useQueryFilters(table);

    const drawer = useCustomDrawer(() => {
        return (
            <Drawer
                title={mode === "edit" ? t("edit-pbj") : t("assign-pbj")}
                open={visible}
                close={close}
                style={{
                    maxWidth: "initial",
                }}
            >
                <PBJForm
                    mode={mode}
                    values={selected}
                    close={close}
                    post={post}
                    edit={edit}
                    loading={loading}
                />
            </Drawer>
        );
    });

    const { hasAccess: _canAssignPBJ } = useAccess(
        isSelf ? "profile.pbjCode.canCreate" : "people.pbjCode.canCreate"
    );
    const canAssignPBJ = isOpenFromTimesheet ? false : _canAssignPBJ;

    useEffect(() => {
        const controller = new AbortController();
        fetch(formatParams(queryFilters), { controller });
        return () => controller.abort();
    }, [fetch, queryFilters])

    return (
        <>
            <CardHeader>
                <div className="d-flex justify-content-end">
                    <Button
                        onClick={() => open()}
                        className="border-dark pr-3 btn-round btn-icon shadow-none btn btn-none btn-sm h-25"
                        disabled={!canAssignPBJ}
                    >
                        {t("assign-PBJ")}
                    </Button>
                </div>
            </CardHeader>

            <div>
                <Table table={table} />
            </div>

            {drawer}
            {deleteModal}
        </>
    );
};

export default PBJ;